import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { Link } from 'react-router-dom';

const LOGIN_URL = 'https://my.tdfxcapital.net/portal/login';
const SIGNUP_URL = 'https://my.tdfxcapital.net/portal';

const marketsMenu = [
  { name: 'Forex', path: '/markets/forex', description: 'Trade major, minor & exotic pairs' },
  { name: 'Commodities', path: '/markets/commodities', description: 'Gold, Silver, Oil & more' },
  { name: 'Indices', path: '/markets/indices', description: 'S&P 500, NASDAQ, DAX & more' },
  { name: 'Stocks', path: '/markets/stocks', description: 'Global equities trading' },
  { name: 'Cryptocurrencies', path: '/markets/crypto', description: 'Bitcoin, Ethereum & altcoins' },
];

const platformsMenu = [
  { name: 'MetaTrader 5', path: '/platforms/mt5', description: 'Professional desktop platform' },
  { name: 'WebTrader', path: '/platforms/web', description: 'Trade from any browser' },
  { name: 'Mobile Trading', path: '/platforms/mobile', description: 'iOS & Android apps' },
];

const accountsMenu = [
  { name: 'Account Types', path: '/accounts/types', description: 'Cent, Pro & ECN accounts' },
  { name: 'Funding Methods', path: '/accounts/funding', description: 'Deposits & withdrawals' },
  { name: 'Account Comparison', path: '/accounts/compare', description: 'Find your perfect account' },
];

const companyMenu = [
  { name: 'About Us', path: '/about', description: 'Our story and mission' },
  { name: 'Why TD Capital', path: '/why-us', description: 'Our competitive advantages' },
  { name: 'Security & Compliance', path: '/security', description: 'Security & compliance' },
  { name: 'Contact Us', path: '/contact', description: 'Get in touch with us' },
];

const toolsMenu = [
  { name: 'Economic Calendar', path: '/tools/economic-calendar', description: 'Track market-moving events' },
  { name: 'Trading Calculator', path: '/tools/calculator', description: 'Calculate pips, margin & profit' },
  { name: 'Education Center', path: '/tools/education', description: 'Learn to trade like a pro' },
  { name: 'Market Analysis', path: '/tools/analysis', description: 'Daily insights & forecasts' },
];

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogin = () => {
    window.open(LOGIN_URL, '_blank');
  };

  const handleSignup = () => {
    window.open(SIGNUP_URL, '_blank');
  };

  return (
    <nav
      className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100'
          : 'bg-white'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center">
            <img
              src="https://customer-assets.emergentagent.com/job_speedsite/artifacts/ucp499sm_TD%20Logo.png"
              alt="TD Capital"
              className="h-[72px] w-auto"
            />
          </Link>

          {/* Desktop Navigation with Dropdowns */}
          <div className="hidden lg:flex items-center">
            <NavigationMenu>
              <NavigationMenuList className="space-x-2">
                {/* Markets Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger>
                    Markets
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 bg-white">
                      {marketsMenu.map((item) => (
                        <li key={item.path}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={item.path}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50"
                            >
                              <div className="text-sm font-medium leading-none text-gray-900">{item.name}</div>
                              <p className="line-clamp-2 text-sm leading-snug text-gray-500">
                                {item.description}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Platforms Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger>
                    Platforms
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 bg-white">
                      {platformsMenu.map((item) => (
                        <li key={item.path}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={item.path}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50"
                            >
                              <div className="text-sm font-medium leading-none text-gray-900">{item.name}</div>
                              <p className="line-clamp-2 text-sm leading-snug text-gray-500">
                                {item.description}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Accounts Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger>
                    Accounts
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 bg-white">
                      {accountsMenu.map((item) => (
                        <li key={item.path}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={item.path}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50"
                            >
                              <div className="text-sm font-medium leading-none text-gray-900">{item.name}</div>
                              <p className="line-clamp-2 text-sm leading-snug text-gray-500">
                                {item.description}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Company Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger>
                    Company
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 bg-white">
                      {companyMenu.map((item) => (
                        <li key={item.path}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={item.path}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50"
                            >
                              <div className="text-sm font-medium leading-none text-gray-900">{item.name}</div>
                              <p className="line-clamp-2 text-sm leading-snug text-gray-500">
                                {item.description}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Tools Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger>
                    Tools
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 bg-white">
                      {toolsMenu.map((item) => (
                        <li key={item.path}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={item.path}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50"
                            >
                              <div className="text-sm font-medium leading-none text-gray-900">{item.name}</div>
                              <p className="line-clamp-2 text-sm leading-snug text-gray-500">
                                {item.description}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Partners Link */}
                <NavigationMenuItem>
                  <Link
                    to="/partners"
                    className="group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary transition-colors"
                  >
                    Partners
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="hidden lg:flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-gray-700"
              onClick={handleLogin}
            >
              Log In
            </Button>
            <Button 
              size="sm" 
              className="btn-premium text-white font-semibold"
              onClick={handleSignup}
            >
              Start Trading
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon">
                {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] overflow-y-auto">
              <nav className="flex flex-col space-y-4 mt-8">
                {/* Mobile Markets */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Markets</h3>
                  {marketsMenu.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className="block py-2 text-sm text-gray-700 hover:text-primary"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>

                {/* Mobile Platforms */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Platforms</h3>
                  {platformsMenu.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className="block py-2 text-sm text-gray-700 hover:text-primary"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>

                {/* Mobile Accounts */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Accounts</h3>
                  {accountsMenu.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className="block py-2 text-sm text-gray-700 hover:text-primary"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>

                {/* Mobile Company */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Company</h3>
                  {companyMenu.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className="block py-2 text-sm text-gray-700 hover:text-primary"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>

                {/* Mobile Tools */}
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Tools</h3>
                  {toolsMenu.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className="block py-2 text-sm text-gray-700 hover:text-primary"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>

                <Link
                  to="/partners"
                  onClick={() => setMobileOpen(false)}
                  className="font-bold text-gray-900"
                >
                  Partners
                </Link>

                <div className="flex flex-col space-y-3 pt-4 border-t">
                  <Button variant="outline" className="w-full" onClick={handleLogin}>
                    Log In
                  </Button>
                  <Button className="w-full btn-premium text-white" onClick={handleSignup}>
                    Start Trading
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;